import React, { useEffect, useRef, useState } from "react";

export const OFFERS = {
  follow: {
    title: "Follow my portfolio",
    text: "Each month I'll email you what I invested in, and why.",
    button: "Follow",
    doneTitle: "You're following!",
    doneText: "Thanks! You'll be one of the first to get my portfolio updates.",
  },
  starter: {
    title: "Get my starter portfolio",
    text: "Where should I send your copy?",
    button: "Send me a copy",
    doneTitle: "It's on its way!",
    doneText: "Thanks! I'll email your copy of my starter portfolio shortly.",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function track(name, props) {
  try {
    if (window.amplitude && typeof window.amplitude.track === "function") {
      window.amplitude.track(name, props || {});
    }
  } catch (_) {}
}

/**
 * Email-capture popup. Render it only while an offer is open, keyed by the
 * offer, so each opening starts fresh:
 *   {offer && <OfferPopup key={offer} offerKey={offer} onClose={() => setOffer(null)} />}
 */
export default function OfferPopup({ offerKey, onClose }) {
  const offer = OFFERS[offerKey];
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [done, setDone] = useState(false);

  const inputRef = useRef(null);
  const doneButtonRef = useRef(null);

  // Remember what had focus before opening, and give it back on close.
  useEffect(() => {
    const lastFocus = document.activeElement;
    return () => {
      if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
    };
  }, []);

  // Focus the email field on open, and the Done button after submitting.
  useEffect(() => {
    if (done) doneButtonRef.current?.focus();
    else inputRef.current?.focus();
  }, [done]);

  // Only count it as a dismissal if they close without submitting.
  const handleClose = () => {
    if (!done) track("storefront_offer_dismissed", { offer: offerKey });
    onClose();
  };

  // Escape closes. Kept in a ref so the listener always sees the latest state.
  const closeRef = useRef(handleClose);
  closeRef.current = handleClose;
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setShowError(true);
      inputRef.current?.focus();
      return;
    }
    setShowError(false);
    track("storefront_email_submitted", { email: value, offer: offerKey });
    setDone(true);
  };

  if (!offer) return null;

  return (
    <div
      className="overlay"
      id="overlay"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) handleClose();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="sheetTitle" id="sheet">
        {!done ? (
          <>
            <div className="sheet-head">
              <div>
                <h2 id="sheetTitle">{offer.title}</h2>
                <p style={{ marginTop: 4 }}>{offer.text}</p>
              </div>
              <button type="button" className="x" aria-label="Close" onClick={handleClose}>
                ×
              </button>
            </div>

            <form id="emailForm" noValidate onSubmit={handleSubmit}>
              <div className="field">
                <label htmlFor="emailInput">Email</label>
                <input
                  ref={inputRef}
                  id="emailInput"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  aria-invalid={showError || undefined}
                  aria-describedby={showError ? "emailError" : undefined}
                />
              </div>
              <p className="error" id="emailError" hidden={!showError}>
                Enter a valid email address, like name@example.com.
              </p>
              <button type="submit" className="btn" style={{ marginTop: 14 }}>
                {offer.button}
              </button>
              <p className="small" style={{ marginTop: 10 }}>
                I'll only use your email for this. Unsubscribe anytime.
              </p>
            </form>
          </>
        ) : (
          <>
            <div className="sheet-head">
              <div className="done-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="m5 12 5 5 9-10" />
                </svg>
              </div>
              <button type="button" className="x" aria-label="Close" onClick={handleClose}>
                ×
              </button>
            </div>
            <h2 id="sheetTitle">{offer.doneTitle}</h2>
            <p>{offer.doneText}</p>
            <button ref={doneButtonRef} type="button" className="btn" onClick={handleClose}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
